import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApiService } from '@/features/auth/api/auth.api';
import type { SocialAccount, SocialProvider } from '@/features/auth/types/auth.types';

/** The user's attached social accounts (read from the auth server's /v1/me). */
export function useSocialAccounts() {
  return useQuery({
    queryKey: ['social-accounts'],
    queryFn: async (): Promise<SocialAccount[]> => {
      const me = await authApiService.me();
      return (me.social_accounts as SocialAccount[] | undefined) ?? [];
    },
  });
}

/**
 * Begin attaching a provider: ask the auth server for a one-time ticket, then
 * redirect the browser to the hosted attach flow. The provider round-trip +
 * the actual attach happen server-side; control returns to /conturi.
 */
export function useAttachSocial() {
  return useMutation({
    mutationFn: (provider: SocialProvider) => authApiService.attachStart(provider),
    onSuccess: ({ redirect_url }) => {
      window.location.href = redirect_url;
    },
  });
}

/** Detach a social account (direct, Bearer-authenticated) and refresh the list. */
export function useDetachSocial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, email }: { provider: SocialProvider | string; email: string }) =>
      authApiService.detachSocialAccount(provider, email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-accounts'] }),
  });
}
